// public/js/features/pois/poi-modal.js
// Shared modal for creating (from a map click) and editing a POI.
// Exposes window.openPoiModal({ mode:'create'|'edit', poi?, lat?, lng? }).
import { poiState } from './poi-state.js';
import { createPoi, updatePoi, deletePoi, loadMyTenants } from './api.js';
import { refreshPoiData } from './poi-data.js';
import { scopeTenantParam } from '../../modules/tenant-scope.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { helpIcon, initHelp } from '../../ui/help.js';
import { mountOverlaySection } from './poi-overlay-editor.js';

// In-app help for the POI modal — see docs/help-text.md.
const HELP = {
  shortName:
    '<b>Short name</b><br>' +
    'A compact label shown on the <b>map pin</b> and used to name locations in the asset list ' +
    '(Last Position). Keep it a few characters so it stays readable at a glance.' +
    '<br><br><b>Aviation:</b> use the ICAO/IATA code — <i>KMIA</i> or <i>MIA</i> — instead of ' +
    '“Miami International Airport”.',
  geoRadius:
    '<b>Geofence radius (m)</b><br>' +
    'Draws a <b>circle</b> around this POI and enables <b>enter/exit alerts</b> (Alerts → Geofence). ' +
    'Leave empty for no geofence; a geofence rule targeting a POI without a radius falls back to 500 m.' +
    '<br><br><b>Aviation:</b> a 300 m circle around a hangar warns you the moment a tug tows an aircraft out.',
  geoPolygon:
    '<b>Geofence polygon</b><br>' +
    'A hand-drawn <b>shape</b> for fences that aren’t round. When present it <b>overrides the radius</b> — ' +
    'on the map and for alerts. <b>Draw:</b> tap the map to drop points, drag a numbered point to ' +
    'move it, then press <b>Done</b> in the toolbar on the map. <b>Edit</b> reopens the saved shape, ' +
    'so one corner can be corrected without redrawing. The polygon is stored when you <b>Save</b> the POI.' +
    '<br><br><b>Aviation:</b> trace the exact apron boundary so “on the ramp” means the ramp, not a circle ' +
    'that spills onto the taxiway.',
};

let modalInstance = null;
let myTenantsCache = null; // { canCreatePublic, tenants: [{tenantId,name}] }

export function createPoiModal() {
  if (document.getElementById('poiModal')) return;

  const html = `
    <div class="modal fade" id="poiModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="poi-modal-title">New POI</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3" id="poi-scope-wrap">
              <label class="form-label fw-semibold">Tenant / Scope</label>
              <select id="poi-scope" class="form-select"></select>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Name <span class="text-danger">*</span></label>
              <input id="poi-name" class="form-control" type="text" maxlength="120">
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Short name${helpIcon('shortName')}</label>
              <input id="poi-shortname" class="form-control" type="text" maxlength="40" placeholder="Shown on the map pin">
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Type <span class="text-danger">*</span></label>
              <select id="poi-type" class="form-select"></select>
            </div>
            <div class="row g-2 mb-3">
              <div class="col">
                <label class="form-label fw-semibold">Latitude</label>
                <input id="poi-lat" class="form-control" type="number" step="any">
              </div>
              <div class="col">
                <label class="form-label fw-semibold">Longitude</label>
                <input id="poi-lng" class="form-control" type="number" step="any">
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Geofence radius (m)${helpIcon('geoRadius')}</label>
              <input id="poi-geofence" class="form-control" type="number" min="20" max="100000" placeholder="empty = no geofence">
              <div class="form-text">Enables enter/exit alerts on this POI (Alerts → Geofence). The circle shows on the map; alerts default to 500 m if a geofence rule targets a POI without a radius.</div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Geofence polygon${helpIcon('geoPolygon')}</label>
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span id="poi-polygon-status" class="text-muted small">No polygon</span>
                <button type="button" id="poi-polygon-draw" class="btn btn-sm btn-outline-primary">
                  <i class="fa-solid fa-draw-polygon me-1"></i><span id="poi-polygon-draw-label">Draw on map</span>
                </button>
                <button type="button" id="poi-polygon-remove" class="btn btn-sm btn-outline-danger d-none">
                  <i class="fa-solid fa-xmark me-1"></i>Remove
                </button>
              </div>
              <div class="form-text">A polygon overrides the radius for alerts and on the map. Tap the map to add points, drag a point to move it, then press Done in the on-map toolbar. Stored when you save the POI.</div>
            </div>
            <div class="mb-2">
              <label class="form-label fw-semibold">Description</label>
              <textarea id="poi-description" class="form-control" rows="2" maxlength="500"></textarea>
            </div>
            <div class="mb-2" id="poi-overlays-section"></div>
          </div>
          <div class="modal-footer justify-content-between">
            <button type="button" id="poi-delete-btn" class="btn btn-outline-danger d-none">
              <i class="fa-solid fa-trash me-1"></i>Delete
            </button>
            <div class="ms-auto">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" id="poi-save-btn" class="btn btn-success">
                <i class="fa-solid fa-save me-1"></i>Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>`;
  document.body.insertAdjacentHTML('beforeend', html);

  const modalEl = document.getElementById('poiModal');
  modalInstance = new bootstrap.Modal(modalEl);
  initHelp(modalEl, HELP); // wire the "?" help icons (modal built once)

  // If drawing navigated the user to #map, closing the modal (save or cancel)
  // returns them to the page they came from. Hidden-for-drawing is exempt.
  modalEl.addEventListener('hidden.bs.modal', () => {
    if (drawingInProgress || !drawReturnHash) return;
    const target = drawReturnHash;
    drawReturnHash = null;
    if (location.hash === '#map') location.hash = target;
  });

  document.getElementById('poi-scope').addEventListener('change', () => {
    renderTypeOptions(document.getElementById('poi-scope').value);
  });

  document.getElementById('poi-save-btn').addEventListener('click', onSave);
  document.getElementById('poi-delete-btn').addEventListener('click', onDelete);
  document.getElementById('poi-polygon-draw').addEventListener('click', onDrawPolygon);
  document.getElementById('poi-polygon-remove').addEventListener('click', () => {
    pendingPolygon = null;
    updatePolygonStatus();
  });

  console.log('✅ POI modal created');
}

let currentMode = 'create';
let currentPoiId = null;
// Polygon edit buffer: undefined = untouched (don't send), null = remove,
// array = newly drawn ring [[lng,lat],…]. The saved poi's polygon is kept in
// currentPolygonSaved for status display.
let pendingPolygon;
let currentPolygonSaved = null;
// When drawing forces a navigation to #map, remember where the user came from
// so closing/saving the modal returns them there.
let drawReturnHash = null;
// True while the modal is hidden FOR drawing (suppresses the return-navigation
// that normally happens when the modal closes).
let drawingInProgress = false;

function effectivePolygon() {
  return pendingPolygon !== undefined ? pendingPolygon : currentPolygonSaved;
}

function updatePolygonStatus() {
  const poly = effectivePolygon();
  const status = document.getElementById('poi-polygon-status');
  const removeBtn = document.getElementById('poi-polygon-remove');
  if (!status) return;
  const unsaved = pendingPolygon !== undefined && !!poly;
  status.innerHTML = poly
    ? `Polygon: ${poly.length} points — overrides the radius`
      + (unsaved ? ' <span class="badge-soft-amber">not saved yet</span>' : '')
    : 'No polygon';
  removeBtn.classList.toggle('d-none', !poly);
  // "Draw" on a blank POI, "Edit" once there is a ring to adjust - the draw
  // mode loads it, so the label would otherwise promise a fresh start.
  const drawLabel = document.getElementById('poi-polygon-draw-label');
  if (drawLabel) drawLabel.textContent = poly ? 'Edit on map' : 'Draw on map';
}

function onDrawPolygon() {
  const lat = parseFloat(document.getElementById('poi-lat').value);
  const lng = parseFloat(document.getElementById('poi-lng').value);
  // The map page wires window.poiDrawPolygon; navigate there first if needed —
  // and remember where the user came from (restored when the modal closes).
  if (location.hash !== '#map') {
    if (!drawReturnHash) drawReturnHash = location.hash || '#assets';
    location.hash = '#map';
  }
  drawingInProgress = true;
  modalInstance.hide();

  const reopen = () => { drawingInProgress = false; modalInstance.show(); };
  let tries = 0;
  const attempt = () => {
    if (window.poiDrawPolygon) {
      window.poiDrawPolygon({
        center: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null,
        // Hand the current ring over so drawing EDITS it instead of starting
        // from nothing - one dragged corner is the common case, not a redraw.
        initial: effectivePolygon(),
        onDone: (vertices) => {
          pendingPolygon = vertices;
          updatePolygonStatus();
          reopen();
          window.showToast?.(`Polygon with ${vertices.length} points ready - press Save to store it`, 'info');
        },
        onCancel: reopen,
      });
    } else if (tries++ < 50) {
      setTimeout(attempt, 100); // map still initialising
    } else {
      window.showToast?.('Map is not available — open the map page and try again', 'warning');
      reopen();
    }
  };
  attempt();
}

window.openPoiModal = async function ({ mode = 'create', poi = null, lat = null, lng = null } = {}) {
  if (!document.getElementById('poiModal')) createPoiModal();
  currentMode = mode;
  currentPoiId = poi?.poiId || null;

  // Make sure we have POI types loaded for the dropdown (cache-first).
  if (!poiState.loaded) { try { await window.poiEnsureData?.(); } catch (e) {} }

  document.getElementById('poi-modal-title').textContent = mode === 'edit' ? 'Edit POI' : 'New POI';
  document.getElementById('poi-name').value = poi?.name || '';
  document.getElementById('poi-shortname').value = poi?.shortName || '';
  document.getElementById('poi-lat').value = poi?.lat ?? (lat != null ? Number(lat).toFixed(6) : '');
  document.getElementById('poi-lng').value = poi?.lng ?? (lng != null ? Number(lng).toFixed(6) : '');
  document.getElementById('poi-description').value = poi?.description || '';
  document.getElementById('poi-geofence').value = poi?.geofenceRadiusM ?? '';
  pendingPolygon = undefined;
  currentPolygonSaved = Array.isArray(poi?.geofencePolygon) && poi.geofencePolygon.length >= 3 ? poi.geofencePolygon : null;
  drawReturnHash = null;
  drawingInProgress = false;
  updatePolygonStatus();

  await renderScopeOptions(mode, poi);
  renderTypeOptions(document.getElementById('poi-scope').value, poi?.typeId);

  // Delete button only in edit mode and when permitted.
  const groups = getCurrentUser().groups || [];
  const isPublic = (poi?.scope === 'public');
  const canDelete = mode === 'edit' && (isPublic ? can(groups, 'managePublicPois') : can(groups, 'deletePoi'));
  document.getElementById('poi-delete-btn').classList.toggle('d-none', !canDelete);

  // Read-only when editing a POI the user may not mutate (e.g. a public POI for
  // a non-platform user) — inputs disabled + Save hidden, so it can't be edited.
  const canMutate = mode === 'create'
    ? true
    : (isPublic ? can(groups, 'managePublicPois') : can(groups, 'updatePoi'));
  const readOnly = mode === 'edit' && !canMutate;
  ['poi-name', 'poi-shortname', 'poi-type', 'poi-lat', 'poi-lng', 'poi-description', 'poi-geofence',
    'poi-polygon-draw', 'poi-polygon-remove']
    .forEach(id => { const el = document.getElementById(id); if (el) el.disabled = readOnly; });
  document.getElementById('poi-save-btn').classList.toggle('d-none', readOnly);
  document.getElementById('poi-modal-title').textContent =
    mode === 'edit' ? (readOnly ? 'View POI' : 'Edit POI') : 'New POI';

  // Custom map overlays — only for an existing POI the user may edit (upload
  // needs a saved poiId). See docs/poi-custom-maps.md.
  const ovSection = document.getElementById('poi-overlays-section');
  if (ovSection) {
    if (mode === 'edit' && canMutate) {
      mountOverlaySection(ovSection, {
        poiId: currentPoiId,
        overlays: poi?.overlays || [],
        hide: () => modalInstance.hide(),
        show: () => modalInstance.show(),
        onChanged: () => { refreshPoiData(); },
      });
    } else {
      ovSection.innerHTML = mode === 'create'
        ? '<div class="form-text">Save the POI first, then reopen it to add map overlays.</div>'
        : '';
    }
  }

  modalInstance.show();
};

async function renderScopeOptions(mode, poi) {
  const wrap = document.getElementById('poi-scope-wrap');
  const select = document.getElementById('poi-scope');

  if (mode === 'edit') {
    // Scope is fixed when editing.
    if (poi.scope !== 'public' && !myTenantsCache) {
      try { myTenantsCache = await loadMyTenants(); } catch (e) { myTenantsCache = { canCreatePublic: false, tenants: [] }; }
    }
    const label = poi.scope === 'public' ? 'Public (all tenants)' : tenantName(poi.scope);
    select.innerHTML = `<option value="${poi.scope}">${escapeHtml(label)}</option>`;
    select.disabled = true;
    select.classList.toggle('tenant-blur', poi.scope !== 'public'); // blur a real tenant name in demo mode
    wrap.classList.remove('d-none');
    return;
  }

  select.disabled = false;
  if (!myTenantsCache) {
    try { myTenantsCache = await loadMyTenants(); } catch (e) { myTenantsCache = { canCreatePublic: false, tenants: [] }; }
  }
  const opts = [];
  myTenantsCache.tenants.forEach(t => opts.push(`<option value="${t.tenantId}">${escapeHtml(t.name || t.tenantId)}</option>`));
  if (myTenantsCache.canCreatePublic) opts.push(`<option value="public">Public (all tenants)</option>`);
  select.innerHTML = opts.join('');

  // Global tenant scope: under a single scope preselect that tenant (a new POI
  // must not vanish from the current view). The picker stays visible for users
  // who may also create public POIs.
  const scopeTid = scopeTenantParam();
  const scopePinned = scopeTid && scopeTid !== 'unassigned' && myTenantsCache.tenants.some(t => t.tenantId === scopeTid);
  if (scopePinned) select.value = scopeTid;

  // Hide the scope picker entirely when there is exactly one choice, or when the
  // scope pins it and no public option competes.
  wrap.classList.toggle('d-none', opts.length <= 1 || (scopePinned && !myTenantsCache.canCreatePublic));
}

function renderTypeOptions(scope, selectedTypeId = null) {
  const select = document.getElementById('poi-type');
  const available = poiState.types.filter(t => t.scope === 'public' || t.scope === scope);

  // The POI's OWN type must always be in the list. When the locally cached type
  // list is behind - a colleague created the type an hour ago - the option is
  // missing, the browser falls back to showing the FIRST entry as selected, and
  // pressing Save silently retypes the POI to that one. So keep a placeholder
  // carrying the real id, and refresh in the background to get its name.
  if (selectedTypeId && !available.some(t => t.poiId === selectedTypeId)) {
    available.unshift({ poiId: selectedTypeId, name: 'Loading type...', scope });
    refreshPoiData().then(() => {
      if (document.getElementById('poiModal')?.classList.contains('show')) {
        renderTypeOptions(scope, selectedTypeId);
      }
    }).catch(() => {});
  }

  if (available.length === 0) {
    select.innerHTML = `<option value="">No types available — create one in Settings</option>`;
    return;
  }
  select.innerHTML = available.map(t =>
    `<option value="${t.poiId}" ${t.poiId === selectedTypeId ? 'selected' : ''}>${escapeHtml(t.name)}</option>`
  ).join('');
}

async function onSave() {
  const btn = document.getElementById('poi-save-btn');
  const original = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>Saving...`;
  try {
    // Commit any fully-calibrated overlay the user set up but didn't explicitly
    // "Save overlay" — so it isn't lost by clicking the POI Save instead.
    try { await document.getElementById('poi-overlays-section')?.__overlayCommitPending?.(); } catch (e) {}

    const name = document.getElementById('poi-name').value.trim();
    const typeId = document.getElementById('poi-type').value;
    const lat = parseFloat(document.getElementById('poi-lat').value);
    const lng = parseFloat(document.getElementById('poi-lng').value);
    if (!name) throw new Error('Name is required');
    if (!typeId) throw new Error('Please pick a type');
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('Valid coordinates are required');

    const geofenceRaw = document.getElementById('poi-geofence').value.trim();
    const payload = {
      name,
      shortName: document.getElementById('poi-shortname').value.trim(),
      typeId,
      lat, lng,
      description: document.getElementById('poi-description').value.trim(),
      // Empty clears the geofence (backend clamps 20 m…100 km).
      geofenceRadiusM: geofenceRaw ? parseInt(geofenceRaw) : null,
    };
    // Polygon only when touched: newly drawn ring or explicit removal (null).
    if (pendingPolygon !== undefined) payload.geofencePolygon = pendingPolygon;

    if (currentMode === 'edit') {
      await updatePoi(currentPoiId, payload);
      window.showToast('POI updated', 'success');
    } else {
      payload.scope = document.getElementById('poi-scope').value;
      await createPoi(payload);
      window.showToast('POI created', 'success');
    }

    modalInstance.hide();
    await window.poiRefreshData?.();
    window.refreshPoiList?.();
  } catch (err) {
    console.error(err);
    window.showToast(err.message || 'Failed to save POI', 'danger');
  } finally {
    btn.disabled = false;
    btn.innerHTML = original;
  }
}

async function onDelete() {
  if (!currentPoiId) return;
  if (!confirm('Delete this POI?')) return;
  const btn = document.getElementById('poi-delete-btn');
  btn.disabled = true;
  try {
    await deletePoi(currentPoiId);
    window.showToast('POI deleted', 'success');
    modalInstance.hide();
    await window.poiRefreshData?.();
    window.refreshPoiList?.();
  } catch (err) {
    window.showToast(err.message || 'Failed to delete POI', 'danger');
  } finally {
    btn.disabled = false;
  }
}

function tenantName(tenantId) {
  const t = (myTenantsCache?.tenants || []).find(x => x.tenantId === tenantId);
  return t?.name || tenantId;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
